import React, { useState } from 'react';
import { PenLine, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../../ui';
import { Input, Textarea } from '../../ui-v2.5';

/**
 * NotaDeHistorial — el cuerpo de un evento `nota` en el Timeline, con su edición y su borrado en
 * línea (19/10, Fase 2b).
 *
 * Los botones SÓLO se enseñan si el servidor lo dice en el propio evento (`nota_editable`,
 * `nota_borrable`, `nota_requiere_motivo`): lo calcula la misma regla que luego decide el PATCH o
 * el DELETE (notasService.permisoSobreNota). Aquí no se decide nada: si pasa el plazo con el panel
 * abierto, el servidor responde 403 y se enseña su mensaje.
 *
 * El texto, SIEMPRE plano: React lo escapa y `whitespace-pre-wrap` respeta los saltos de línea.
 */

// El mismo límite que el servidor (eventValidator y notasService: 5000).
export const NOTA_MAX = 5000;

/**
 * Lo que NO va en una nota (19/10, decidido con Iban). Las notas las lee cualquiera que vea el
 * activo y se guardan con sus versiones: un dato de salud o un motivo personal se quedaría ahí. Se
 * escribe el hecho («baja», «cambio de titular»), no la causa. Debajo de las DOS cajas, crear y
 * editar: es el mismo texto y aquí está una sola vez.
 */
export function AyudaNota() {
  return (
    <p className="mt-1 text-[11px] text-slate-400">
      No incluyas datos de salud ni motivos personales. Escribe «baja» o «cambio de titular», no la causa.
    </p>
  );
}

const errorDe = (e, porDefecto) => e?.response?.data?.error || e?.response?.data?.message || e?.message || porDefecto;

export default function NotaDeHistorial({ ev, onEditar, onBorrar }) {
  const [modo, setModo] = useState(null); // null | 'editar' | 'borrar'
  const [texto, setTexto] = useState(ev.notas || '');
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const requiere = !!ev.nota_requiere_motivo;
  const puedeEditar = !!ev.nota_editable && typeof onEditar === 'function';
  const puedeBorrar = !!ev.nota_borrable && typeof onBorrar === 'function';
  const limpio = texto.trim();
  const faltaMotivo = requiere && !motivo.trim();

  function abrir(m) {
    setModo(m); setError(null); setMotivo(''); setTexto(ev.notas || '');
  }

  async function confirmar() {
    setBusy(true); setError(null);
    try {
      if (modo === 'editar') await onEditar(ev.id, limpio, motivo.trim() || null);
      else await onBorrar(ev.id, motivo.trim() || null);
      setModo(null);
    } catch (e) {
      setError(errorDe(e, modo === 'editar' ? 'No se pudo guardar la nota.' : 'No se pudo borrar la nota.'));
    } finally {
      setBusy(false);
    }
  }

  const campoMotivo = (
    <Input
      label={requiere ? 'Motivo *' : 'Motivo (opcional)'}
      value={motivo}
      maxLength={2000}
      onChange={(e) => setMotivo(e.target.value)}
      placeholder={modo === 'borrar' ? '¿Por qué se borra?' : '¿Por qué se cambia?'}
      disabled={busy}
    />
  );
  const botones = (textoConfirmar, desactivado, peligro) => (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button variant="outline" onClick={() => setModo(null)} disabled={busy} className="w-full sm:w-auto">Cancelar</Button>
      <Button variant={peligro ? 'danger' : 'primary'} onClick={confirmar} disabled={busy || desactivado} className="w-full sm:w-auto">
        {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
        {textoConfirmar}
      </Button>
    </div>
  );

  if (modo === 'editar') {
    return (
      <div className="mt-1.5 space-y-2 rounded-md border border-amber-200 bg-amber-50/50 p-3">
        <Textarea
          label="Texto de la nota"
          rows={4}
          maxLength={NOTA_MAX}
          value={texto}
          onChange={(e) => { setTexto(e.target.value); if (error) setError(null); }}
          hint={`${texto.length.toLocaleString('es-ES')} / ${NOTA_MAX.toLocaleString('es-ES')}`}
          error={error}
          disabled={busy}
        />
        <AyudaNota />
        {campoMotivo}
        {botones('Guardar', !limpio || limpio === (ev.notas || '') || faltaMotivo, false)}
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      <p className="whitespace-pre-wrap break-words rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-800">
        {ev.notas}
      </p>
      {modo === 'borrar' ? (
        <div className="mt-2 space-y-2 rounded-md border border-rose-200 bg-rose-50/60 p-3">
          <p className="text-sm text-slate-700">
            ¿Borrar esta nota? Dejará de verse en el historial; queda registrada para auditoría.
          </p>
          {campoMotivo}
          {error && <p className="text-[11px] text-red-600">{error}</p>}
          {botones('Borrar nota', faltaMotivo, true)}
        </div>
      ) : (puedeEditar || puedeBorrar) && (
        <div className="mt-1 flex gap-3">
          {puedeEditar && (
            <button type="button" onClick={() => abrir('editar')}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
              <PenLine className="w-3.5 h-3.5" /> Editar
            </button>
          )}
          {puedeBorrar && (
            <button type="button" onClick={() => abrir('borrar')}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-rose-600">
              <Trash2 className="w-3.5 h-3.5" /> Borrar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
